import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, TrendingUp, Users, Briefcase, BookOpen } from 'lucide-react';

const Home = () => {
  const { t } = useLanguage();

  const pathCards = [
    {
      path: '/investors',
      icon: TrendingUp,
      title: t('For Investors', 'Para Inversores'),
      description: t(
        'Strategic acceleration capital opportunity. €330K seed round, 0.66% dilution, 30-month timeline to licensing.',
        'Oportunidad de capital de aceleración estratégica. Ronda seed de €330K, 0.66% dilución, 30 meses hasta licenciamiento.'
      ),
      color: 'from-blue-600 to-blue-700',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
    },
    {
      path: '/grants',
      icon: Briefcase,
      title: t('Public Grants', 'Subvenciones Públicas'),
      description: t(
        'Strategic alignment with CDTI, PERTE Health, Horizon Europe, and Next Generation EU funding programs.',
        'Alineación estratégica con programas CDTI, PERTE Salud, Horizonte Europa y Next Generation EU.'
      ),
      color: 'from-green-600 to-green-700',
      bgColor: 'bg-green-50',
      textColor: 'text-green-700',
    },
    {
      path: '/partners',
      icon: Users,
      title: t('For Partners', 'Para Socios'),
      description: t(
        'Collaboration opportunities for pharmaceutical companies, CROs, and academic institutions.',
        'Oportunidades de colaboración para farmacéuticas, CROs e instituciones académicas.'
      ),
      color: 'from-purple-600 to-purple-700',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-700',
    },
    {
      path: '/warcliv',
      icon: BookOpen,
      title: t('Warcliv (Educational)', 'Warcliv (Educacional)'),
      description: t(
        'Educational resources for patients, families, and healthcare professionals. Understanding cancer metabolism.',
        'Recursos educativos para pacientes, familias y profesionales de la salud. Comprendiendo el metabolismo del cáncer.'
      ),
      color: 'from-amber-500 to-amber-600',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-700',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1F4E78] via-[#2E5090] to-[#1F4E78] text-white overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        {/* Background Image */}
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://customer-assets.emergentagent.com/job_metasigna-biotech/artifacts/6l9hdbud_1sZ0FXLvtTHql12HlN6_.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            {t(
              'Transforming Tumor Metabolism into Selective Therapeutic Advantage',
              'Transformando el Metabolismo Tumoral en Ventaja Terapéutica Selectiva'
            )}
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-4xl mx-auto">
            {t(
              'Investigational first-in-class dual-function metabolic platform - vectorial and therapeutic - designed to deliver more effective and less toxic treatments for solid tumors, leukemias, and rare cancers. Accelerated regulatory pathways may be available through drug repurposing strategies.',
              'Plataforma metabólica de doble función investigacional de primera clase - vectorial y terapéutica - diseñada para ofrecer tratamientos más efectivos y menos tóxicos para tumores sólidos, leucemias y tumores raros. Vías regulatorias aceleradas pueden estar disponibles mediante estrategias de reposicionamiento de fármacos.'
            )}
          </p>

          {/* Therapeutic Objective */}
          <div className="bg-gradient-to-r from-[#FFD700]/20 to-amber-500/20 backdrop-blur-sm rounded-xl p-8 border border-[#FFD700]/40 max-w-4xl mx-auto mt-8">
            <h3 className="text-lg font-semibold text-[#FFD700] mb-2 uppercase tracking-wide">
              {t('Our Therapeutic Objective', 'Nuestro Objetivo Terapéutico')}
            </h3>
            <p className="text-2xl md:text-3xl font-bold text-white mb-4">
              {t(
                'Better drugs, more effective and less toxic',
                'Mejores fármacos, más efectivos y menos tóxicos'
              )}
            </p>
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-[#FFD700]">1.2M - 4.2M</div>
                <div className="text-gray-200 mt-2">
                  {t('Life-years gained annually', 'Años de vida ganados anualmente')}
                </div>
              </div>
              <div className="hidden md:block h-16 w-px bg-white/30"></div>
              <div className="text-sm text-gray-300 text-center md:text-left max-w-xs">
                {t(
                  'From conservative to high-impact scenario',
                  'De escenario conservador a alto impacto'
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Orphan Indications & High Unmet Need */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-r from-rose-50 to-amber-50 rounded-2xl p-8 md:p-10 border border-rose-100">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-amber-500 rounded-xl flex items-center justify-center">
                  <span className="text-3xl">💊</span>
                </div>
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-[#1F4E78] mb-4">
                  {t(
                    'Broad Spectrum with Strategic Orphan Focus',
                    'Amplio Espectro con Foco Estratégico en Huérfanas'
                  )}
                </h3>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  {t(
                    'The Metasigna platform enables development of metabolically-targeted therapies across a broad spectrum of solid tumors and hematological malignancies. Within this scope, we strategically prioritize orphan indications and oncology subpopulations with critical unmet medical need —such as glioblastoma, certain aggressive leukemias, or metastatic triple-negative breast cancer subgroups— where accelerated regulatory pathways offer a clear competitive advantage and potentially faster access to patients.',
                    'La plataforma Metasigna permite el desarrollo de terapias metabólicamente dirigidas a un amplio espectro de tumores sólidos y neoplasias hematológicas. En este contexto, priorizamos estratégicamente indicaciones huérfanas y subpoblaciones oncológicas con necesidad médica crítica no cubierta —como el glioblastoma, determinadas leucemias agresivas o subgrupos de cáncer de mama triple negativo metastásico— donde las vías regulatorias aceleradas ofrecen una ventaja competitiva clara y un acceso potencialmente más rápido a los pacientes.'
                  )}
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="px-4 py-2 bg-rose-100 text-rose-700 rounded-full text-sm font-medium">
                    {t('Glioblastoma', 'Glioblastoma')}
                  </span>
                  <span className="px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                    {t('Aggressive Leukemias', 'Leucemias Agresivas')}
                  </span>
                  <span className="px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                    {t('Triple-Negative Breast Cancer', 'Cáncer de Mama Triple Negativo')}
                  </span>
                  <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                    {t('Accelerated Pathways', 'Vías Aceleradas')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Overview */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-[#1F4E78]">
            {t('Platform Overview', 'Resumen de la Plataforma')}
          </h2>
          
          {/* Platform Image */}
          <div className="mb-12 flex justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_metasigna-biotech/artifacts/a4d6hwdm_1Qfls7ot5ZNupsO3VqmIu.png"
              alt={t('Immune cell response - Metabolic targeting concept', 'Respuesta celular inmune - Concepto de direccionamiento metabólico')}
              className="rounded-xl shadow-lg max-w-xs w-full h-auto"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-[#1F4E78] rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-[#1F4E78]">
                {t('Dual-Function: Vectorial + Therapeutic', 'Doble Función: Vectorial + Terapéutica')}
              </h3>
              <p className="text-gray-600">
                {t(
                  'Our investigational metabolic ligands are designed to simultaneously target tumors (vectorial function) AND disrupt tumor metabolism (therapeutic function) - a dual mechanism inspired by FDG-PET imaging.',
                  'Nuestros ligandos metabólicos investigacionales están diseñados para simultáneamente dirigirse a tumores (función vectorial) Y interrumpir el metabolismo tumoral (función terapéutica) - un mecanismo dual inspirado en la imagen FDG-PET.'
                )}
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-[#1F4E78] rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">🧬</span>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-[#1F4E78]">
                {t('Broad Applicability', 'Amplia Aplicabilidad')}
              </h3>
              <p className="text-gray-600">
                {t(
                  'Platform approach enables multiple investigational candidates. Orphan drug and drug repurposing strategies may provide accelerated regulatory pathways (subject to regulatory approval).',
                  'El enfoque de plataforma permite múltiples candidatos investigacionales. Las estrategias de fármacos huérfanos y reposicionamiento pueden proporcionar vías regulatorias aceleradas (sujeto a aprobación regulatoria).'
                )}
              </p>
            </div>
            <Link 
              to="/investors#leadership" 
              className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 hover:shadow-lg hover:border-[#1F4E78] transition-all duration-300 block"
            >
              <div className="w-16 h-16 rounded-full overflow-hidden mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_med-innovation-2/artifacts/0gnivrte_IMG_7822.jpeg"
                  alt="Dr. Ángel Norro"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-[#1F4E78]">
                {t('Founder', 'Fundador')}
              </h3>
              <p className="text-gray-600">
                {t(
                  'Dr. Ángel Norro: 30+ years surgeon + serial entrepreneur (50-clinic network) + academic. Self-funded ~€35K + POC ongoing.',
                  'Dr. Ángel Norro: Cirujano 30+ años + emprendedor serial (red de 50 clínicas) + académico. Autofinanciado ~€35K + POC en curso.'
                )}
              </p>
            </Link>
          </div>
          
          {/* Learn More CTA */}
          <div className="mt-8 text-center">
            <Link 
              to="/warcliv" 
              className="inline-flex items-center space-x-2 text-[#2E5090] hover:text-[#1F4E78] font-semibold transition-colors"
            >
              <span>{t('Learn more about the scientific foundation', 'Conoce más sobre la fundamentación científica')}</span>
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Path Selection */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-[#1F4E78]">
            {t('Choose Your Path', 'Elija su Camino')}
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            {t(
              'Explore opportunities tailored to your interests: investment, public funding, or strategic partnerships.',
              'Explore oportunidades adaptadas a sus intereses: inversión, financiación pública o alianzas estratégicas.'
            )}
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {pathCards.map((card) => {
              const Icon = card.icon;
              return (
                <Link
                  key={card.path}
                  to={card.path}
                  className="group bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className={`h-2 bg-gradient-to-r ${card.color}`}></div>
                  <div className="p-8">
                    <div className={`w-16 h-16 ${card.bgColor} rounded-lg flex items-center justify-center mb-6`}>
                      <Icon size={32} className={card.textColor} />
                    </div>
                    <h3 className="text-2xl font-bold mb-4 text-[#1F4E78] group-hover:text-[#2E5090] transition-colors">
                      {card.title}
                    </h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">{card.description}</p>
                    <div className="flex items-center text-[#2E5090] font-semibold group-hover:text-[#1F4E78] transition-colors">
                      {t('Learn More', 'Más Información')}
                      <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
